import "./index.css";

const MONTHS_BD = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const formatDate = (ms) => {
  if (!ms) return "—";
  const d = new Date(isNaN(Number(ms)) ? ms : Number(ms));
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getDate()} ${MONTHS_BD[d.getMonth()]} ${d.getFullYear()}`;
};

/* Handles both ISO date strings ("2024-01-15") and numeric ms timestamps */
const fmtExpiry = (v) => {
  if (!v) return null;
  const d = new Date(isNaN(Number(v)) ? v : Number(v));
  if (isNaN(d.getTime())) return null;
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS_BD[d.getMonth()]} ${d.getFullYear()}`;
};

const isExpiredDate = (v) => {
  if (!v) return false;
  const d = new Date(isNaN(Number(v)) ? v : Number(v));
  return !isNaN(d.getTime()) && d < new Date();
};

const fmt = (v, fallback = "—") => {
  const n = parseFloat(v);
  return isNaN(n)
    ? fallback
    : `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const BundleDetail = ({ bundle, onBack }) => {
  if (!bundle) return null;

  const title = bundle?.title || bundle?.name || "Untitled bundle";
  const desc = bundle?.description || bundle?.shortDescription || "";
  const startDate = formatDate(bundle?.bundleStartDate);
  const endDate = formatDate(bundle?.bundleEndDate);
  const price = bundle?.bundlePrice;
  const sellingPrice =
    bundle?.externalSalesChannels?.[0]?.salePrice ||
    bundle?.variants?.[0]?.externalSalesChannels?.[0]?.salePrice;
  const barcode = bundle?.bundleBarcode;
  const totalQty = bundle?.bundleQuantity;
  const variants = bundle?.variants || [];
  /* IMS composition quantities keyed by product gtin (authoritative per-item bundle qty).
     The catalog variants do not carry bundleQuantity, only available stock. */
  const imsProducts = bundle?.imsBundle?.bundleProducts || [];
  const now = Date.now();
  const endDateMs = bundle?.bundleEndDate
    ? new Date(
        isNaN(Number(bundle?.bundleEndDate)) ? bundle?.bundleEndDate : Number(bundle?.bundleEndDate)
      ).getTime()
    : null;
  const startDateMs = bundle?.bundleStartDate
    ? new Date(
        isNaN(Number(bundle?.bundleStartDate))
          ? bundle?.bundleStartDate
          : Number(bundle?.bundleStartDate)
      ).getTime()
    : null;
  const active = endDateMs ? endDateMs >= now : true;

  /* Dates not yet started */
  const notStarted = startDateMs ? startDateMs > now : false;
  const availability = notStarted ? "Upcoming" : active ? "Active" : "Expired";
  const availClass = notStarted
    ? "bd-badge--upcoming"
    : active
    ? "bd-badge--active"
    : "bd-badge--expired";

  return (
    <div className="bd-root">
      {/* ── Back header ── */}
      <div className="bd-topbar">
        <button type="button" className="bd-back-btn" onClick={onBack} aria-label="Back to bundles">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          Bundles
        </button>
      </div>

      <div className="bd-body">
        {/* ── Hero card ── */}
        <div className="bd-hero">
          <div className="bd-hero-title-row">
            <h2 className="bd-title">{title}</h2>
            <span className={`bd-badge ${availClass}`}>{availability}</span>
          </div>

          {desc && <p className="bd-desc">{desc}</p>}

          <div className="bd-meta-grid">
            {/* MRP */}
            <div className="bd-meta-cell">
              <span className="bd-meta-label">Bundle MRP</span>
              <span className="bd-meta-value bd-meta-value--price">
                {price != null
                  ? `₹${parseFloat(price).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
                  : "—"}
              </span>
            </div>

            {/* Selling price */}
            <div className="bd-meta-cell">
              <span className="bd-meta-label">Bundle selling price</span>
              <span className="bd-meta-value bd-meta-value--price">
                {sellingPrice != null && sellingPrice !== ""
                  ? `₹${parseFloat(sellingPrice).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                    })}`
                  : "—"}
              </span>
            </div>

            {/* Validity */}
            <div className="bd-meta-cell">
              <span className="bd-meta-label">Valid from</span>
              <span className="bd-meta-value">{startDate}</span>
            </div>
            <div className="bd-meta-cell">
              <span className="bd-meta-label">Valid until</span>
              <span className="bd-meta-value">{endDate}</span>
            </div>

            {/* Barcode */}
            {barcode && (
              <div className="bd-meta-cell">
                <span className="bd-meta-label">Bundle barcode</span>
                <span className="bd-meta-value bd-meta-value--mono">{barcode}</span>
              </div>
            )}

            {/* Total qty */}
            {totalQty != null && (
              <div className="bd-meta-cell">
                <span className="bd-meta-label">Bundle quantity</span>
                <span className="bd-meta-value">{totalQty}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Products table ── */}
        <div className="bd-products-section">
          <div className="bd-section-title">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.29 7 12 12 20.71 7" />
              <line x1="12" y1="22" x2="12" y2="12" />
            </svg>
            Bundle composition
            <span className="bd-section-count">
              {variants?.length} product{variants?.length !== 1 ? "s" : ""}
            </span>
          </div>

          {variants?.length === 0 ? (
            <div className="bd-no-products">No product details available for this bundle.</div>
          ) : (
            <div className="bd-table-wrap">
              <table className="bd-table">
                <thead>
                  <tr>
                    <th className="bd-th bd-th--product">Product</th>
                    <th className="bd-th bd-th--num">Qty</th>
                    <th className="bd-th bd-th--num">MRP</th>
                    <th className="bd-th bd-th--num">Purchase</th>
                    <th className="bd-th bd-th--num">Selling</th>
                  </tr>
                </thead>
                <tbody>
                  {variants?.map((v, i) => {
                    const vName = v?.name || "—";
                    const uom = v?.weightUnit || v?.sellingUnit || "—";
                    const inv = v?.inventorySync || {};
                    /* Quantity added to the bundle (not available stock).
                       Primary source: IMS bundleProducts (matched by gtin). The catalog
                       may return bundleQuantity as "" (empty string) so use || not ?? */
                    const imsQty = imsProducts?.find(
                      (p) =>
                        p?.gtin != null &&
                        (p.gtin === inv?.gtin ||
                          p.gtin === v?.barcodes?.[0] ||
                          (v?.barcodes || []).includes(p?.gtin))
                    )?.quantity;
                    const qty =
                      imsQty ?? (inv?.bundleQuantity || v?.bundleQuantity || v?.quantity || "—");
                    const mrp = fmt(inv.mrp ?? v?.mrpData?.[0]?.mrp);
                    const purchase = fmt(inv.purchasePrice);
                    const selling = fmt(inv.sellingPrice);
                    /* Expiry: inv.expiry is unix seconds from bundle API (0 = no expiry) */
                    const expiryMs =
                      inv.expiry && Number(inv.expiry) > 0 ? Number(inv.expiry) * 1000 : null;
                    const expiryLabel = fmtExpiry(expiryMs);
                    const itemExpired = isExpiredDate(expiryMs);
                    /* Batch used during bundle creation */
                    const batchId = inv.batchNo || inv.batchId || inv.batchNum || null;

                    return (
                      <tr key={v?.variantId || i} className="bd-tr">
                        <td className="bd-td bd-td--product">
                          <p className="bd-prod-name">{vName}</p>
                          {v?.barcodes?.[0] && <p className="bd-prod-barcode">{v?.barcodes[0]}</p>}
                          <div className="bd-prod-pills">
                            <span className="bd-uom-pill">{uom}</span>
                            {batchId && <span className="bd-batch-pill">#{batchId}</span>}
                            {expiryLabel && (
                              <span
                                className={`bd-expiry-pill${
                                  itemExpired ? " bd-expiry-pill--expired" : ""
                                }`}
                              >
                                Exp: {expiryLabel}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="bd-td bd-td--num">{qty}</td>
                        <td className="bd-td bd-td--num">{mrp}</td>
                        <td className="bd-td bd-td--num">{purchase}</td>
                        <td className="bd-td bd-td--num">{selling}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BundleDetail;
